import { stateToHTML } from 'draft-js-export-html'
import kebabCase from 'lodash/kebabCase'
import { exportMergeTag, getImageSrc } from './editor-utils'
import inlineStyleFn from './inline-style'

const camelToKebab = (object: Record<string, any>) => {
  const result = {}

  for (const key in object) {
    result[kebabCase(key)] = object[key]
  }

  return result
}

const getOptions = (settings: ExportSettings) => {
  return {
    inlineStyleFn,
    blockStyleFn: block => {
      if (block.getType() === 'atomic') return undefined
      const blockAlignment =
        block.getData() && block.getData().get('text-align')

      if (blockAlignment) {
        return {
          style: {
            textAlign: blockAlignment.replace('align-', ''),
          },
        }
      }

      return undefined
    },
    entityStyleFn: entity => {
      const entityType = entity.get('type').toLowerCase()

      if (entityType === 'button') {
        const data = entity.getData()
        const { link, style, align } = data
        return {
          element: 'mj-button',
          attributes: {
            href: link,
            ...camelToKebab(style),
            padding: '5px 0px',
            'font-weight': '600',
            'inner-padding': '10px 25px',
            align: align || undefined,
          },
        }
      }

      if (entityType === 'image') {
        const data = entity.getData()
        const { width, link: href, align, alt } = data
        return {
          element: 'mj-image',
          attributes: {
            src: getImageSrc(data),
            width: width ? `${width}px` : undefined,
            href,
            padding: `0 ${settings.imagePadding || 25}px`,
            align: align || undefined,
            alt: `${alt || ' '}`, // space is added intentionally here to keep alt attribute in resulting html
          },
        }
      }

      if (entityType === 'link') {
        const data = entity.getData()
        const { target, url } = data
        return {
          element: 'a',
          attributes: {
            target,
            href: url,
            class: 'text-link',
          },
        }
      }

      if (entityType === 'merge_tag') {
        return {
          element: 'span',
          attributes: {
            'data-merge-tag-text': exportMergeTag(entity),
          },
        }
      }
    },
  }
}

type ExportSettings = {
  baseFontSize?: string
  imagePadding?: string
  baseFontFamily?: string
  lineHeight?: number
}

const parseMergeTags = base => {
  const parser = new DOMParser()
  const htmlBase = parser.parseFromString(base, 'text/html')
  const mergeTags = htmlBase.querySelectorAll('[data-merge-tag-text]')

  for (const tag of mergeTags) {
    let child = tag.firstChild
    const content = tag.getAttribute('data-merge-tag-text') || ''

    if (child) {
      while (child && child.hasChildNodes()) {
        child = child.firstChild
      }

      if (child) {
        child.textContent = content
      }
      tag.outerHTML = tag.innerHTML
    } else {
      tag.textContent = content
      tag.removeAttribute('data-merge-tag-text')
    }
  }

  return htmlBase
}

export default function exportToMjml(
  contentState: Record<string, any>,
  settings: ExportSettings,
): string {
  const base = stateToHTML(contentState, getOptions(settings))
  const parsedBase = parseMergeTags(base)
  const { baseFontSize, baseFontFamily } = settings
  const mjButtonReplacement = `<mj-button ${
    baseFontFamily ? `font-family="${baseFontFamily}"` : ''
  } ${baseFontSize ? `font-size="${baseFontSize.replace('px', '')}px"` : ''} `
  return parsedBase.body.innerHTML
    .replace(/<br>/g, '<br/>') // br tags must be self closing in mjml
    .replace(/<p([^>]*)><br\/><\/p>/g, '<p$1>&nbsp;<br/></p>') // except it's needed to fix line spacing in Outlook 2019 in certain cases
    .replace(/<p(?=[\s>])/g, '<p class="text-paragraph"')
    .replace(/<{{(.+?)}}>.+?<\/{{.+?}}>/g, '{{$1}}')
    .replace(/>\s<\/mj-image>/, '/>')
    .replace(/<mj-button\s/g, mjButtonReplacement)
}
